// src/components/organisms/PatientWorkspaceTabs/PhoneCallEditor.js
//
// Telefon görüşmesi editörü — düz editör dili (bkz. _editorParts).

import React, { useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import { format } from "date-fns";

import FormTextField from "../../atoms/FormTextField";
import FormCheckbox from "../../atoms/FormCheckbox";
import ActionButton from "../../atoms/ActionButton";
import { useClinicTheme } from "../../../theme";
import { COLORS } from "../../../styles/uiConstants";
import useInlineAutosave from "../../../hooks/useInlineAutosave";

import {
  useCreatePhoneCallMutation,
  useUpdatePhoneCallMutation,
} from "../../../store/secretaryApi";
import FormDatePicker from "../../atoms/FormDatePicker";
import { EDITOR_PX, SECTION_SX, FieldRow, EditorHeader } from "./_editorParts";
import useTerms from "../../../hooks/useTerms";

const phoneCallFieldsFromRecord = (record) => ({
  date: record?.date
    ? format(new Date(record.date), "yyyy-MM-dd")
    : format(new Date(), "yyyy-MM-dd"),
  subject: record?.subject || "",
  description: record?.description || "",
  caller: record?.caller || "",
  callback: Boolean(record?.callback),
});

export default function PhoneCallEditor({
  record,
  mode = "session",
  patientId,
  token,
  examinationId,
  canEdit,
  onCreated,
  onSaved,
  onDelete,
  registerAutoSave,
  registerFlush,
  onSaveStatusChange,
  onError,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();

  const [createPhoneCall] = useCreatePhoneCallMutation();
  const [updatePhoneCall] = useUpdatePhoneCallMutation();

  const createFn = useCallback(
    async (payload) => {
      const res = await createPhoneCall(payload).unwrap();
      return res?.data?.phoneCall || res?.phoneCall || res;
    },
    [createPhoneCall]
  );
  const updateFn = useCallback(
    async (payload) => {
      const res = await updatePhoneCall(payload).unwrap();
      return res?.data?.phoneCall || res?.phoneCall || res;
    },
    [updatePhoneCall]
  );

  const useManualCreate = mode === "standalone";

  const { fieldValues, setField, saveStatus, saveNow } = useInlineAutosave({
    record,
    createMutation: createFn,
    updateMutation: updateFn,
    fieldsFromRecord: phoneCallFieldsFromRecord,
    shouldCreate: (form) => Boolean(form.subject && form.subject.trim()),
    createTriggerField: useManualCreate ? null : "subject",
    createDebounceMs: 400,
    debounceMs: 500,
    manualCreate: useManualCreate,
    buildPayload: (form) => ({
      token,
      patientId,
      ...(examinationId ? { examinationId } : {}),
      date: form.date,
      subject: form.subject,
      description: form.description,
      caller: form.caller,
      callback: Boolean(form.callback),
    }),
    recordIdField: "phoneCallId",
    onCreated,
    onSaved,
    onError,
    registerAutoSave,
    registerFlush,
  });

  useEffect(() => {
    if (typeof onSaveStatusChange === "function")
      onSaveStatusChange(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  const isDisabled = !canEdit;
  const isNew = !record?._id;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <EditorHeader
        record={record}
        newLabel="Yeni telefon görüşmesi oluşturuluyor"
        editLabel="Telefon görüşmesi düzenleniyor"
        saveStatus={useManualCreate ? undefined : saveStatus}
        palette={palette}
        actions={
          !isNew && !isDisabled && typeof onDelete === "function" ? (
            <ActionButton variant="delete" tone="soft" compact label="Sil" onClick={() => onDelete(record)} />
          ) : null
        }
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {/* Görüşme */}
        <Box sx={SECTION_SX}>
          <FieldRow label="Tarih">
            <FormDatePicker
              value={fieldValues.date || ""}
              onChange={(v) => setField("date", v)}
              disabled={isDisabled}
              clearable={false}
              variant="standard"
              palette={palette}
            />
          </FieldRow>
          <FieldRow label="Konu *">
            <FormTextField
              palette={palette}
              value={fieldValues.subject || ""}
              onChange={(e) => setField("subject", e.target.value)}
              disabled={isDisabled}
              variant="standard"
              size="small"
              fullWidth
              placeholder="Görüşme konusu"
              inputProps={{ "aria-label": "Konu" }}
            />
          </FieldRow>
          <FieldRow label="Arayan">
            <FormTextField
              palette={palette}
              value={fieldValues.caller || ""}
              onChange={(e) => setField("caller", e.target.value)}
              disabled={isDisabled}
              variant="standard"
              size="small"
              fullWidth
              placeholder={`Ör. ${terms.patientGen} kendisi, kızı`}
              inputProps={{ "aria-label": "Arayan" }}
            />
          </FieldRow>
          <FieldRow label="Geri dönüş">
            <FormCheckbox
              checked={!!fieldValues.callback}
              onChange={(v) => setField("callback", v)}
              disabled={isDisabled}
              label={`${terms.patientDat} geri dönülmesi gerekiyor`}
            />
          </FieldRow>
        </Box>

        {/* Görüşme özeti */}
        <Box sx={{ ...SECTION_SX, borderBottom: "none" }}>
          <FieldRow label="Görüşme özeti">
            <FormTextField
              palette={palette}
              value={fieldValues.description || ""}
              onChange={(e) => setField("description", e.target.value)}
              disabled={isDisabled}
              variant="standard"
              size="small"
              fullWidth
              multiline
              minRows={5}
              placeholder="Görüşmede konuşulanlar"
              inputProps={{ "aria-label": "Görüşme özeti" }}
            />
          </FieldRow>
        </Box>
      </Box>

      {/* Bağımsız sayfada kayıt elle kaydedilir (muayenede otomatik) */}
      {!isDisabled && useManualCreate && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            ...EDITOR_PX,
            py: 1.5,
            borderTop: `1px solid ${COLORS.slate100}`,
          }}
        >
          <ActionButton
            variant="save"
            palette={palette}
            onClick={() => saveNow()}
            disabled={!fieldValues.subject?.trim() || saveStatus === "saving"}
            loading={saveStatus === "saving"}
            label={isNew ? "Görüşmeyi Kaydet" : "Kaydet"}
          />
        </Box>
      )}
    </Box>
  );
}
